<template>
  <div>
    <v-btn
      small
      text
      color="primary"
      class="mr-2"
      @click="showExportDialog"
    >
      <v-icon
        small
        class="mr-1"
      >
        mdi-file-excel
      </v-icon>
      {{ $t('excelExport') }}
    </v-btn>

    <!-- Export Confirmation Dialog -->
    <v-dialog
      v-model="dialogVisible"
      max-width="500"
    >
      <v-card>
        <v-card-title class="headline">
          {{ $t('confirmExport') || 'Confirm Export' }}
        </v-card-title>
        <v-card-text>
          <p>{{ $t('exportLimitationsMessage') || 'Please note that some columns will not be exported:' }}</p>
          <ul>
            <li>{{ $t('imageColumnsNotExported') || 'Image columns will not be exported' }}</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey darken-1"
            text
            @click="dialogVisible = false"
          >
            {{ $t('cancel') || 'Cancel' }}
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="confirmExport"
          >
            {{ $t('confirm') || 'Confirm' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ExportJsonExcel from 'js-export-excel'
import IKDataEntity from '@/plugins/EntityFrameWork/IKDataEntity'
import { getNiceRestaurantInfo } from '@/model/dataLayer/service/otherService'

export default {
  name: 'ExcelExport',
  props: {
    headers: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    fileName: {
      type: String,
      default: 'export'
    }
  },
  data () {
    return {
      dialogVisible: false
    }
  },
  methods: {
    showExportDialog () {
      this.dialogVisible = true
    },

    confirmExport () {
      this.dialogVisible = false
      this.exportToExcel()
    },
    async exportToExcel () {
      // Get restaurant info
      const restaurantInfo = await getNiceRestaurantInfo()
      const restaurantName = (restaurantInfo.name || '').replace(/\s+/g, '') // Remove spaces

      // Format current date and time as timestamp in YYYYMMDD-HHMMSS format
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      const timestamp = `${year}${month}${day}-${hours}${minutes}${seconds}` // Format: YYYYMMDD-HHMMSS

      // Combine restaurant name, timestamp, and original filename
      const formattedFileName = `${restaurantName}_${timestamp}_${this.fileName}`

      const option = {
        fileName: formattedFileName,
        datas: [
          {
            sheetName: 'Sheet1',
            sheetData: this.processData(),
            sheetHeader: this.processHeaders(),
            columnWidths: this.headers.map(() => 15)
          }
        ]
      }

      const toExcel = new ExportJsonExcel(option)
      toExcel.saveExcel()
    },

    processHeaders () {
      return this.headers
        .filter(header => header.visible !== false)
        .sort((a, b) => a.order - b.order)
        .filter(header => header.value !== 'action') // Exclude action column
        .filter(header => {
          // Exclude unsupported column types (Image and Images)
          return header.dataType !== IKDataEntity.Types.Image &&
                header.dataType !== IKDataEntity.Types.Images
        })
        .map(header => header.text || header.value)
    },

    processData () {
      const visibleHeaders = this.headers
        .filter(header => header.visible !== false)
        .sort((a, b) => a.order - b.order)
        .filter(header => header.value !== 'action') // Exclude action column
        .filter(header => {
          // Exclude unsupported column types (Image and Images)
          return header.dataType !== IKDataEntity.Types.Image &&
                header.dataType !== IKDataEntity.Types.Images
        })

      return this.items.map(item => {
        const row = {}
        visibleHeaders.forEach(header => {
          const value = this.getValueByType(item, header)
          row[header.text || header.value] = value
        })
        return row
      })
    },

    getValueByType (item, header) {
      const value = item[header.value]

      // Handle null or undefined values
      if (value === undefined || value === null) {
        return ''
      }

      // Use switch for different data types
      switch (header.dataType) {
        case IKDataEntity.Types.Option: {
          // Check if there's a corresponding display text
          const optValue = item['opt' + header.value]
          if (optValue !== undefined) {
            // Handle both single values and arrays
            if (Array.isArray(value)) {
              // For arrays, join the display texts with commas
              return [optValue].flat().join(', ')
            }
            return optValue
          }
          break
        }

        case IKDataEntity.Types.Boolean:
          return value ? 'Yes' : 'No'

        case IKDataEntity.Types.Date: {
          if (value) {
            try {
              const date = new Date(value)
              return date.toLocaleDateString()
            } catch (e) {
              return value
            }
          }
          break
        }

        case IKDataEntity.Types.Color:
          return value // Return color code as is

        case IKDataEntity.Types.Image:
        case IKDataEntity.Types.Images:
          return value // Return image URL as is

        case IKDataEntity.Types.Group: {
          // For group type, we'll return a simplified representation
          if (Array.isArray(value)) {
            return value.map(v => String(v)).join(', ')
          }
          break
        }

        // No special handling needed for these types
        case IKDataEntity.Types.Integer:
        case IKDataEntity.Types.Float:
          return value
      }

      // Handle by value type for cases not handled by dataType
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No'
      }

      if (typeof value === 'number') {
        return value
      }

      if (typeof value === 'object') {
        return JSON.stringify(value)
      }

      // Default: return as string
      return String(value)
    }
  }
}
</script>
